'use client';

import { useRef } from 'react';
import { ProductSelectionSection } from 'components/m1/ProductSelection';
import { JsonLd, TypeformSnippet } from 'components/shared';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import {
  ExperienceVideoCarouselSection,
  PersonaCardsSection,
} from 'components/home';
import { BottomBar } from 'components/m1/BottomBar';
import { newM1reviews, reviewsForAllSection_m1 } from 'content/m1';
import { FeaturedReviews } from 'components/m1/FeaturedReviews';
import { M1Reviews } from 'components/m1/M1Reviews';
import { M1SelectionProvider } from 'components/m1/M1SelectionProvider';
import { MasterYourMetabolism } from 'components/m1/MasterYourMetabolism';
import { Ogdb } from 'components/m1/Ogdb';
import { ResearchSection } from 'components/m1/ResearchSection';
import { SensorFeatures } from 'components/m1/SensorFeatures';
import { FAQSection } from 'components/shared/FAQSection';
import { ProductWizard } from 'components/shared/ProductWizard';
import { getM1PricingSchemas } from 'lib/seo/meta/pricing/m1';
import { isMCFCountry } from 'lib/region/countryUtils';
import {
  pricingFaqData_us,
  pricingFaqData_in,
  pricingFaqData_ae,
  pricingFaqData_gb_eu,
  pricingFaqData_intl,
} from 'content/pricingFaqData';
import { getM1Config } from './m1-config';

function getOffersData(region: string) {
  switch (region) {
    case 'US':
      return [
        {
          title: (
            <>
              Instant payment with <b>PayPal, Apple Pay, or GPay</b>
            </>
          ),
          image: [
            'web_v2/icons/offers/paypal.png',
            'web_v2/icons/offers/apple.png',
            'web_v2/icons/offers/google.png',
          ],
        },
      ];
    case 'IN':
      return [
        {
          title: (
            <>
              Easy payments with <b>UPI and Wallets</b>
            </>
          ),
          image: ['web_v2/icons/offers/upi.png'],
        },
        {
          title: (
            <>
              <b>CRED Pay</b> | Earn rewards, pay later options
            </>
          ),
          image: ['web_v2/icons/offers/cred.png'],
        },
        {
          title: (
            <>
              <b>No cost EMI</b> starting at{' '}
              <span style={{ whiteSpace: 'nowrap' }}>₹ 312.46/month</span>
            </>
          ),
          image: ['web_v2/icons/offers/0-percent.png'],
        },
      ];
    case 'AE':
      return [
        {
          title: (
            <>
              <b>Split payments</b> with Tabby, Tamara or Cashew
            </>
          ),
          image: ['web_v2/icons/offers/split-payment.png'],
        },
        {
          title: (
            <>
              Instant payment with <b>Apple Pay</b>
            </>
          ),
          image: ['web_v2/icons/offers/apple-pay.png'],
        },
        {
          title: (
            <>
              <b>Free Cash on Delivery</b>
            </>
          ),
          image: ['web_v2/icons/offers/location.png'],
        },
      ];
    default:
      return undefined; // MCF/NON-MCF pages don't show offers
  }
}

function getFaqData(region: string) {
  switch (region) {
    case 'US':
      return pricingFaqData_us;
    case 'IN':
      return pricingFaqData_in;
    case 'AE':
      return pricingFaqData_ae;
    default:
      if (isMCFCountry(region)) return pricingFaqData_gb_eu;
      return pricingFaqData_intl;
  }
}

// Effective price of the page's default selection — the number M1SelectionProvider
// renders first. Passed to the JSON-LD schema so the structured-data price matches
// the visible lead price (US now defaults to the $99 membership). undefined on miss.
function getEffectiveDefaultPrice(
  config: NonNullable<ReturnType<typeof getM1Config>>,
): number | undefined {
  const price =
    config.products?.[config.defaultProductOption]?.plans?.[
      config.defaultProduct
    ]?.price;
  return typeof price === 'number' && Number.isFinite(price)
    ? price
    : undefined;
}

export interface PricingPageProps {
  region: string;
  language: string;
  store: string;
  currency: string;
  isM1Available: boolean;
  showWaitlist: boolean;
}

export default function Pricing({
  region,
  store,
  currency,
  // isM1Available,
  showWaitlist,
}: PricingPageProps) {
  const reviewsRef = useRef<HTMLDivElement>(null);

  const regionLower = region.toLowerCase();

  // Waitlist: show embedded typeform instead of M1 pricing
  if (showWaitlist) {
    return (
      <div style={{ width: '100%', height: '100vh', backgroundColor: '#000' }}>
        <TypeformSnippet
          typeformId="gmPmrIhN"
          frameTitle="M1 Live Waitlist"
          className="typeform-container"
          style={{ width: '100%', height: '100vh' }}
        />
      </div>
    );
  }

  const m1Config = getM1Config(region);
  if (!m1Config) return null;

  const m1PricingSchemas = getM1PricingSchemas({
    country: regionLower,
    store: (store as ShopifyStore) || ShopifyStore.ROW,
    // Price + currency both sourced from m1Config so the schema matches the
    // currency the visible page renders (M1SelectionProvider uses m1Config.currency).
    currency: m1Config.currency || (currency as Currency) || Currency.usd,
    basePriceOverride: getEffectiveDefaultPrice(m1Config),
  });
  const offersData = getOffersData(region);
  const faqData = getFaqData(region);
  const showSensorFeatures = region === 'IN';

  return (
    <>
      <JsonLd data={m1PricingSchemas} />
      <M1SelectionProvider
        currency={m1Config.currency}
        store={m1Config.store}
        locale={'locale' in m1Config ? m1Config.locale : undefined}
        defaultProduct={m1Config.defaultProduct}
        defaultProductOption={m1Config.defaultProductOption}
        products={m1Config.products}
        outOfStock={'outOfStock' in m1Config ? m1Config.outOfStock : undefined}
        mcfDeliveryEstimate={
          'mcfDeliveryEstimate' in m1Config
            ? m1Config.mcfDeliveryEstimate
            : undefined
        }
      >
        <ProductSelectionSection offersData={offersData} />
        <FeaturedReviews
          handleClick={() =>
            reviewsRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
        />
        {showSensorFeatures && <SensorFeatures />}
        <MasterYourMetabolism />
        <Ogdb />
        <ResearchSection />
        <PersonaCardsSection />
        <ExperienceVideoCarouselSection showButton={false} />
        <ProductWizard
          title={`Still unsure if ${region === 'US' ? 'M2' : 'M1'} Live is the right wearable for you?`}
        />
        <div ref={reviewsRef}>
          <M1Reviews
            title="User Reviews"
            app_store={reviewsForAllSection_m1.app_store}
            play_store={reviewsForAllSection_m1.play_store}
            items={newM1reviews}
          />
        </div>
        <FAQSection faqData={faqData} bgColor="white" />
        <BottomBar />
      </M1SelectionProvider>
    </>
  );
}
